import { useId } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export interface AlertTimelineChartPoint {
  formattedTime: string;
  total: number;
  critical?: number;
  high?: number;
  medium?: number;
  low?: number;
}

interface AlertTimelineComposedChartProps {
  data: AlertTimelineChartPoint[];
  heightClassName?: string;
  margin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-card/95 backdrop-blur-sm border border-border rounded-xl p-4 shadow-xl">
        <p className="text-sm font-semibold text-foreground mb-2">{label}</p>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground">Total</span>
            <span className="text-sm font-bold text-foreground">{data.total}</span>
          </div>
          {data.critical > 0 && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-red-500">Critical</span>
              <span className="text-sm font-medium text-red-500">{data.critical}</span>
            </div>
          )}
          {data.high > 0 && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-orange-500">High</span>
              <span className="text-sm font-medium text-orange-500">{data.high}</span>
            </div>
          )}
          {data.medium > 0 && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-yellow-600">Medium</span>
              <span className="text-sm font-medium text-yellow-600">{data.medium}</span>
            </div>
          )}
          {data.low > 0 && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-green-600">Low</span>
              <span className="text-sm font-medium text-green-600">{data.low}</span>
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};

export default function AlertTimelineComposedChart({
  data,
  heightClassName = "h-64",
  margin = { top: 20, right: 20, left: -10, bottom: 0 },
}: AlertTimelineComposedChartProps) {
  const gradientIdSuffix = useId().replace(/:/g, "");

  return (
    <div className={heightClassName}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={margin}>
          <defs>
            <linearGradient id={`alertAreaGradient-${gradientIdSuffix}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(217, 91%, 50%)" stopOpacity={0.25} />
              <stop offset="100%" stopColor="hsl(217, 91%, 50%)" stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id={`barGradient-${gradientIdSuffix}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(217, 91%, 55%)" stopOpacity={0.9} />
              <stop offset="100%" stopColor="hsl(217, 91%, 45%)" stopOpacity={0.7} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground))" opacity={0.3} />
          <XAxis
            dataKey="formattedTime"
            stroke="hsl(var(--muted-foreground))"
            fontSize={11}
            tickLine={false}
            axisLine={false}
            dy={10}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
          />
          <YAxis
            stroke="hsl(var(--muted-foreground))"
            fontSize={11}
            tickLine={false}
            axisLine={false}
            dx={-5}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
            allowDecimals={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(var(--muted)/0.1)" }} />
          <Bar dataKey="total" fill={`url(#barGradient-${gradientIdSuffix})`} radius={[4, 4, 0, 0]} maxBarSize={40} />
          <Line
            type="monotone"
            dataKey="total"
            stroke="hsl(217, 91%, 50%)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5, fill: "hsl(217, 91%, 50%)", stroke: "white", strokeWidth: 2 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
